// Shared vocabulary and pure metric helpers for the sales pipeline: the page, board, drawer and
// modal all derive their numbers here rather than each answering "what is this deal worth".

// Pipeline stages, in funnel order. `probability` is a stage's default win chance,
// which a deal may override; `short` is used where horizontal space is tight.
export const STAGES = [
    { key: 'Lead',        label: 'Neu',          short: 'Neu',   hint: 'Lead angelegt, noch nicht kontaktiert', probability: 10 },
    { key: 'Contacted',   label: 'Kontaktiert',  short: 'Kont.', hint: 'Erstkontakt erfolgt (Kaltakquise)',     probability: 25 },
    { key: 'Qualified',   label: 'Qualifiziert', short: 'Qual.', hint: 'Bedarf und Budget bestätigt',           probability: 40 },
    { key: 'Proposal',    label: 'Angebot',      short: 'Ang.',  hint: 'Angebot verschickt',                    probability: 60 },
    { key: 'Negotiation', label: 'Verhandlung',  short: 'Verh.', hint: 'Preis- und Vertragsverhandlung',        probability: 80 },
    { key: 'Won',         label: 'Gewonnen',     short: 'Gew.',  hint: 'Auftrag erhalten',                      probability: 100 },
    { key: 'Lost',        label: 'Verloren',     short: 'Verl.', hint: 'Deal verloren oder abgesagt',           probability: 0 },
];

export const LOST_STAGE = 'Lost';
export const WON_STAGE = 'Won';

/** Stages a deal can still move forward from. */
export const OPEN_STAGES = STAGES.filter(s => s.key !== WON_STAGE && s.key !== LOST_STAGE);

/** The chain a deal walks through, won included — Lost is a drop-out, not a step. */
export const FUNNEL_STAGES = STAGES.filter(s => s.key !== LOST_STAGE);

const STAGE_BY_KEY = Object.fromEntries(STAGES.map(s => [s.key, s]));

export const stageLabel = (key) => STAGE_BY_KEY[key]?.label ?? key ?? '—';
export const stageClass = (key) => `stage-${String(key || '').toLowerCase()}`;
export const stageIndex = (key) => FUNNEL_STAGES.findIndex(s => s.key === key);
export const isOpen = (acq) => acq.status !== WON_STAGE && acq.status !== LOST_STAGE;

/** The stage a deal should move to when it advances, or null at the end of the funnel. */
export const nextStage = (key) => {
    const idx = stageIndex(key);
    if (idx < 0 || idx >= FUNNEL_STAGES.length - 1) return null;
    return FUNNEL_STAGES[idx + 1];
};

export const DEAL_TYPES = [
    { key: 'project', label: 'Projekt', short: 'Projekt' },
    { key: 'product', label: 'Produkt', short: 'Produkt' },
    { key: 'mixed',   label: 'Projekt + Produkt', short: 'Kombi' },
];

export const dealTypeLabel = (key) =>
    DEAL_TYPES.find(t => t.key === key)?.short ?? 'Projekt';

export const LEAD_SOURCES = [
    { key: 'cold_call',  label: 'Kaltakquise Telefon', cold: true },
    { key: 'cold_email', label: 'Kaltakquise E-Mail',  cold: true },
    { key: 'referral',   label: 'Empfehlung' },
    { key: 'inbound',    label: 'Inbound-Anfrage' },
    { key: 'website',    label: 'Website' },
    { key: 'fair',       label: 'Messe / Event' },
    { key: 'network',    label: 'Netzwerk' },
    { key: 'existing',   label: 'Bestandskunde' },
    { key: 'other',      label: 'Sonstige' },
];

export const leadSourceLabel = (key) =>
    LEAD_SOURCES.find(s => s.key === key)?.label ?? (key ? 'Sonstige' : '—');

export const isColdSource = (key) =>
    Boolean(LEAD_SOURCES.find(s => s.key === key)?.cold);

// Units for positions — UN/ECE Rec 20 codes, same as offers/invoices.
export const UNITS = [
    { unit: 'Stk',      unit_code: 'C62', label: 'Stück' },
    { unit: 'h',        unit_code: 'HUR', label: 'Stunde' },
    { unit: 'Tag',      unit_code: 'DAY', label: 'Tag' },
    { unit: 'Monat',    unit_code: 'MON', label: 'Monat' },
    { unit: 'Jahr',     unit_code: 'ANN', label: 'Jahr' },
    { unit: 'Pauschal', unit_code: 'LS',  label: 'Pauschal' },
];

export const unitCodeFor = (unit) =>
    UNITS.find(u => u.unit === unit)?.unit_code ?? 'C62';

export const formatEur = (value, { decimals = 0 } = {}) =>
    Number(value || 0).toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

/** Short money for tight spots: 1.2 Mio. € / 84,5k € / 900 €. */
export const formatEurShort = (value) => {
    const n = Number(value || 0);
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Mio. €`;
    if (Math.abs(n) >= 10_000) return `${Math.round(n / 1000).toLocaleString('de-DE')}k €`;
    return formatEur(n);
};

/** Net total of one position: quantity × unit price minus its discount. */
export const lineTotal = (item = {}) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discount = parseFloat(item.discount_percent) || 0;
    return Math.round(quantity * unitPrice * (1 - discount / 100) * 100) / 100;
};

export const positionsTotal = (items = []) =>
    Math.round((items || []).reduce((sum, item) => sum + lineTotal(item), 0) * 100) / 100;

/** Positions win over the manually entered budget — they are the finer number. */
export const dealValue = (acq = {}) => {
    const items = acq.lineItems || [];
    if (items.length) return positionsTotal(items);
    return Number(acq.expectedBudget || 0);
};

export const probabilityOf = (acq = {}) => {
    if (acq.winProbability !== null && acq.winProbability !== undefined && acq.winProbability !== '') {
        return Number(acq.winProbability);
    }
    return STAGE_BY_KEY[acq.status]?.probability ?? 0;
};

/** Deal value discounted by its win probability. */
export const weightedValue = (acq = {}) =>
    Math.round(dealValue(acq) * probabilityOf(acq)) / 100;

const startOfDay = (value) => {
    const d = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};

/** Whole days from `from` to `to`; negative when `to` lies in the past. */
export const dayDiff = (to, from = new Date()) => {
    const a = startOfDay(to);
    const b = startOfDay(from);
    if (!a || !b) return null;
    return Math.round((a - b) / 86_400_000);
};

/** Follow-up state of a deal; a closed deal never needs one. */
export const followUpState = (acq = {}, today = new Date()) => {
    if (!isOpen(acq)) return 'none';
    if (!acq.nextFollowUpDate) return 'missing';
    const days = dayDiff(acq.nextFollowUpDate, today);
    if (days === null) return 'missing';
    if (days < 0) return 'overdue';
    if (days === 0) return 'today';
    return 'upcoming';
};

export const isFollowUpDue = (acq, today = new Date()) =>
    ['overdue', 'today'].includes(followUpState(acq, today));

/** How long a deal has been sitting in its current stage, in days. */
export const daysInStage = (acq = {}, today = new Date()) => {
    const since = acq.stageChangedAt || acq.updatedAt || acq.createdAt;
    if (!since) return null;
    const days = dayDiff(today, since);
    return days === null ? null : Math.max(0, days);
};

// The daily worklist itself lives in utils/callList.js — it merges deals with leads.

/** Per-stage counts, values and the funnel's step conversion. Stage history is not stored, so
 *  "reached" is derived from where a deal stands today and a lost deal leaves the chain. */
export const funnelStats = (acquisitions = []) => {
    const live = acquisitions.filter(a => a.status !== LOST_STAGE);
    let previousReached = null;
    return FUNNEL_STAGES.map((stage, idx) => {
        const inStage = acquisitions.filter(a => a.status === stage.key);
        const reachedList = live.filter(a => stageIndex(a.status) >= idx);
        const reached = reachedList.length;
        const conversion = previousReached > 0 ? Math.round((reached / previousReached) * 100) : null;
        previousReached = reached;
        return {
            ...stage,
            count: inStage.length,
            value: inStage.reduce((sum, a) => sum + dealValue(a), 0),
            reached,
            reachedValue: reachedList.reduce((sum, a) => sum + dealValue(a), 0),
            conversion,
        };
    });
};

/** Headline numbers for the KPI row. */
export const pipelineKpis = (acquisitions = [], today = new Date()) => {
    const open = acquisitions.filter(isOpen);
    const won = acquisitions.filter(a => a.status === WON_STAGE);
    const lost = acquisitions.filter(a => a.status === LOST_STAGE);
    const openValue = open.reduce((sum, a) => sum + dealValue(a), 0);
    const closed = won.length + lost.length;
    const states = open.map(a => followUpState(a, today));
    return {
        total: acquisitions.length,
        openCount: open.length,
        openValue,
        weightedValue: open.reduce((sum, a) => sum + weightedValue(a), 0),
        avgDealSize: open.length ? openValue / open.length : 0,
        wonCount: won.length,
        wonValue: won.reduce((sum, a) => sum + dealValue(a), 0),
        lostCount: lost.length,
        winRate: closed ? Math.round((won.length / closed) * 100) : null,
        overdueCount: states.filter(s => s === 'overdue').length,
        dueTodayCount: states.filter(s => s === 'today').length,
        missingFollowUpCount: states.filter(s => s === 'missing').length,
        coldCount: open.filter(a => isColdSource(a.leadSource)).length,
    };
};
